import React, { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
// Api
import { SEARCH_TICKET_USERS } from 'api/ticketing/queries';
// Types
import {
  SearchTicketUsers,
  SearchTicketUsersVariables,
} from 'api/ticketing/types/SearchTicketUsers';
// Components
import SelectInfiniteScroll, {
  LoadSelectInfiniteScrollOptionsArgs,
  LoadSelectInfiniteScrollOptionsResult,
} from 'components/common/SelectInfiniteScroll/SelectInfiniteScroll';

type UserSelectProps = {
  value?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  'aria-label'?: string;
};

/** Anyone with an account: comp guests are often fans, not stores. */
const UserSelect = ({
  value,
  onChange,
  placeholder = 'Search by name or email',
  allowClear,
  'aria-label': ariaLabel,
}: UserSelectProps): JSX.Element => {
  const client = useApolloClient();

  const loadOptions = useCallback(
    async ({
      search,
      offset,
      limit,
    }: LoadSelectInfiniteScrollOptionsArgs): Promise<LoadSelectInfiniteScrollOptionsResult> => {
      const term = search.trim();
      const { data } = await client.query<
        SearchTicketUsers,
        SearchTicketUsersVariables
      >({
        query: SEARCH_TICKET_USERS,
        variables: {
          input: {
            offset,
            limit,
            ...(term.includes('@')
              ? { email: term }
              : term
              ? { name: term }
              : {}),
          },
        },
        fetchPolicy: 'network-only',
      });

      return {
        options: (data?.users.entities || []).map((user) => ({
          value: user.id,
          label:
            [
              [user.firstName, user.lastName].filter(Boolean).join(' '),
              user.email,
            ]
              .filter(Boolean)
              .join(' · ') || user.id,
        })),
        total: data?.users.total || 0,
      };
    },
    [client]
  );

  return (
    <SelectInfiniteScroll
      value={value}
      onChange={(next) => onChange?.(next)}
      onClear={() => onChange?.(undefined)}
      allowClear={allowClear}
      loadOptions={loadOptions}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
};

export default UserSelect;
